import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p><a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Multi-factor_authentication"
      }}>{`Two-factor authentication (2FA)`}</a>{` protects against unauthorized access to your account by confirming your identity using:`}</p>
    <ul>
      <li parentName="ul">{`Something you know (e.g., a password).`}</li>
      <li parentName="ul">{`Something you have (e.g., an ID badge or a cryptographic key).`}</li>
      <li parentName="ul">{`Something you are (e.g., a fingerprint or other biometric data).`}</li>
    </ul>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note`}</strong>{`: The security-key flow using `}<a parentName="p" {...{
          "href": "https://webauthn.guide/"
        }}>{`WebAuthn`}</a>{` is currently in beta.`}</p>
    </Note>
    <p>{`When you enable 2FA, you will be prompted for a second form of authentication before performing certain actions on your account or packages to which you have write access. Depending on your 2FA configuration you will be either prompted to authenticate with a `}<a parentName="p" {...{
        "href": "https://webauthn.guide/"
      }}>{`security-key`}</a>{` or a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Time-based_one-time_password"
      }}>{`time-based one-time password (TOTP)`}</a>{`. `}</p>
    <ul>
      <li parentName="ul">{`The security-key flow allows you to use biometric devices such as Apple `}<a parentName="li" {...{
          "href": "https://support.apple.com/en-gb/HT204587"
        }}>{`Touch ID`}</a>{`, `}<a parentName="li" {...{
          "href": "https://support.apple.com/en-us/HT208108"
        }}>{`Face ID`}</a>{` or `}<a parentName="li" {...{
          "href": "https://support.microsoft.com/en-us/windows/learn-about-windows-hello-and-set-it-up-dae28983-8242-bb2a-d3d1-87c9d265a5f0"
        }}>{`Windows Hello`}</a>{` as well as physical keys such as `}<a parentName="li" {...{
          "href": "https://www.yubico.com/"
        }}>{`Yubikey`}</a>{`, `}<a parentName="li" {...{
          "href": "https://thetis.io/"
        }}>{`Thetis`}</a>{` or `}<a parentName="li" {...{
          "href": "https://www.ftsafe.com/"
        }}>{`Feitian`}</a>{` as your 2FA.`}</li>
      <li parentName="ul">{`To configure TOTP you will need to install an authenticator application that can generate OTPs such as `}<a parentName="li" {...{
          "href": "https://authy.com/download/"
        }}>{`Authy`}</a>{`, `}<a parentName="li" {...{
          "href": "https://support.google.com/accounts/answer/1066447"
        }}>{`Google Authenticator`}</a>{`, or `}<a parentName="li" {...{
          "href": "https://www.microsoft.com/security/mobile-authenticator-app"
        }}>{`Microsoft Authenticator`}</a>{` on your mobile device.`}</li>
    </ul>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note:`}</strong>{` Two-factor authentication provides the best possible security for your account against attackers. We strongly recommend enabling 2FA on your account as soon as possible after you sign up.`}</p>
    </Note>
    <h2>{`Two-factor authentication on npm`}</h2>
    <p>{`Two-factor authentication on npm can be enabled for authorization and writes, or authorization only.`}</p>
    <h3>{`Authorization and writes`}</h3>
    <p>{`By default, 2FA is enabled for authorization and writes. We will request a second form of authentication for certain authorized actions, as well as write actions.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Action`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`CLI command`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Log in to npm`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/adduser"
            }}><inlineCode parentName="a">{`npm login`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Change profile settings (including your password)`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/profile"
            }}><inlineCode parentName="a">{`npm profile set`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Change 2FA modes for your user account`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/profile"
            }}><inlineCode parentName="a">{`npm profile enable-2fa auth-and-writes`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Disable 2FA for your user account`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/profile"
            }}><inlineCode parentName="a">{`npm profile disable-2fa`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Create tokens`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/token"
            }}><inlineCode parentName="a">{`npm token create`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Revoke tokens`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/token"
            }}><inlineCode parentName="a">{`npm token revoke`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Publish packages`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/publish"
            }}><inlineCode parentName="a">{`npm publish`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Unpublish packages`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/unpublish"
            }}><inlineCode parentName="a">{`npm unpublish`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Deprecate packages`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/deprecate"
            }}><inlineCode parentName="a">{`npm deprecate`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Change package visibility`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/access"
            }}><inlineCode parentName="a">{`npm access public/restricted`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Change user and team package access`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/access"
            }}><inlineCode parentName="a">{`npm access grant/revoke`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "/requiring-2fa-for-package-publishing-and-settings-modification"
            }}>{`Change package 2FA requirements`}</a></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/access"
            }}><inlineCode parentName="a">{`npm access 2fa-required/2fa-not-required`}</inlineCode></a></td>
        </tr>
      </tbody>
    </table>
    <h3>{`Authorization only`}</h3>
    <p>{`If you enable 2FA for authorization only. We will request a second form of authentication only for certain authorized actions.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Action`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`CLI command`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Log in to npm`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/adduser"
            }}><inlineCode parentName="a">{`npm login`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Change profile settings (including your password)`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/profile"
            }}><inlineCode parentName="a">{`npm profile set`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Change 2FA modes for your user account`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/profile"
            }}><inlineCode parentName="a">{`npm profile enable-2fa auth-only`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Disable 2FA for your user account`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/profile"
            }}><inlineCode parentName="a">{`npm profile disable-2fa`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Create tokens`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/token"
            }}><inlineCode parentName="a">{`npm token create`}</inlineCode></a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Revoke tokens`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><a parentName="td" {...{
              "href": "https://docs.npmjs.com/cli/token"
            }}><inlineCode parentName="a">{`npm token revoke`}</inlineCode></a></td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      